/**
 * Member Timeline tab (DNKADM-0429): one newest-first story per member from
 * `GET /v2/admin/members/{contactId}/timeline` (DNKAPI-0614): bookings, money, credits
 * and refused actions interleaved by the server.
 *
 * - Rows render in API order; nothing here re-sorts, totals or recomputes. Money is
 *   `amountMinor` (unsigned) with the sign taken from `kind` (`moneySign`); a declined or
 *   pending amount moved no money, so it renders muted (`isUnmovedMoney`); credits show
 *   the server's signed `creditChange` and `balance` as given.
 * - Time is tenant-local: the tenant's `defaultTimezone` (the zone the API cuts `from`/`to`
 *   days in). If that read fails the browser zone is the fallback; rows wait for it to
 *   settle so a time never flips zone under the operator.
 * - Paging is the opaque `nextCursor` behind "Load more". Appended rows are de-duplicated
 *   by `id`. Any filter change (a category chip, a date, Clear) drops the cursor AND the
 *   loaded rows and reads page one again; a response from before the change is ignored.
 * - A first-page failure shows the error state with Try again. A Load-more failure KEEPS
 *   the rows already loaded and offers Try again under them.
 * - A kind this build does not know renders its title with no icon.
 * - A declined card payment with a known reason (DNKAPI-0676) shows that reason's line.
 * - DNKAPI-0687: an item tied to an order (comp, void, order payment) offers "View order
 *   <number>", a read-only summary dialog (there is no order route); a folded multi-lot
 *   credit item says "Across N credit lots"; a CREDIT_EXPIRY item shows the lot's own
 *   expiry, since its time is when the row was written.
 *
 * Filters live in component state, not the URL: the member-detail tab itself is not in
 * the URL, so URL params here would outlive the tab they belong to.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Chip, Link, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ListToolbar } from '@/components/list/ListToolbar';
import {
  getMemberTimeline,
  TIMELINE_CATEGORIES,
  type TimelineCategory,
  type TimelineItem,
} from '@/api/memberTimeline';
import { getTenantSettings } from '@/api/tenantSettings';
import { styleForKind } from '@/features/activity/activityKind';
import { formatTransactionTime } from '@/features/transaction-ledger/transactionFormat';
import { useResource } from '@/hooks/useResource';
import { useCalendarZone } from '@/location/useCalendarZone';
import { addDays, zonedDateOf } from '@/components/list/listDate';
import { Icon, inkAlpha, MONO, resolveMaps, resolveTokens } from '@/theme';
import { declineReasonLine } from '@/utils/declineReason';
import { formatCredits, formatMinorMoney } from '@/utils/v2Format';
import { scrubBrand } from '@/utils/whitelabel';
import {
  cancelOutcomeLines,
  isUnmovedMoney,
  lotCountLine,
  lotExpiryLine,
  moneySign,
  REFUSED_ICON,
  signedCredits,
  TIMELINE_CATEGORY_LABELS,
  TIMELINE_COPY,
  timelineActorLabel,
  timelineIconFamily,
  timelineTitle,
} from './memberTimelineCopy';
import { ScreenError } from '@/components/ScreenError';
import { TimelineOrderDialog } from './TimelineOrderDialog';

const SKELETON_ROWS = 5;

interface Filters {
  categories: TimelineCategory[];
  from: string;
  to: string;
}

const NO_FILTERS: Filters = { categories: [], from: '', to: '' };

interface FeedState {
  items: TimelineItem[];
  nextCursor: string | null;
  /** The first page of the current filters. */
  status: 'loading' | 'ready' | 'error';
  error: unknown;
  /** A "Load more" read on top of loaded rows. */
  more: 'idle' | 'loading' | 'error';
}

const LOADING: FeedState = {
  items: [],
  nextCursor: null,
  status: 'loading',
  error: null,
  more: 'idle',
};

/** Append `next` to `prev`, skipping any id already shown (pages never repeat a row). */
function appendUnique(prev: readonly TimelineItem[], next: readonly TimelineItem[]): TimelineItem[] {
  const seen = new Set(prev.map((item) => item.id));
  const out = [...prev];
  for (const item of next) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    out.push(item);
  }
  return out;
}

function hasFilters(filters: Filters): boolean {
  return filters.categories.length > 0 || filters.from !== '' || filters.to !== '';
}

export function MemberTimelineTab({ contactId }: { contactId: string }) {
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [feed, setFeed] = useState<FeedState>(LOADING);
  // Bumped by Try again on a failed first page, to re-run the first-page read.
  const [reloadKey, setReloadKey] = useState(0);
  // Every first-page read starts a new generation; a response from an older one (a
  // filter changed while it was in flight) is dropped, Load more included.
  const generationRef = useRef(0);

  const tenantSettings = useResource(useCallback(() => getTenantSettings(), []));
  const timeZone = tenantSettings.data?.defaultTimezone || null;
  const zoneSettled = tenantSettings.status !== 'loading';

  useEffect(() => {
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    getMemberTimeline(contactId, {
      categories: filters.categories,
      from: filters.from,
      to: filters.to,
    })
      .then((page) => {
        if (generationRef.current !== generation) return;
        setFeed({
          items: appendUnique([], page.items),
          nextCursor: page.nextCursor,
          status: 'ready',
          error: null,
          more: 'idle',
        });
      })
      .catch((error: unknown) => {
        if (generationRef.current !== generation) return;
        setFeed({ ...LOADING, status: 'error', error });
      });
    return () => {
      // Unmount or a newer read: whatever is in flight must not land.
      if (generationRef.current === generation) generationRef.current = generation + 1;
    };
  }, [contactId, filters, reloadKey]);

  /** Every filter change resets the cursor and the list, then reads page one. */
  function changeFilters(next: Filters) {
    setFeed(LOADING);
    setFilters(next);
  }

  function toggleCategory(category: TimelineCategory) {
    const selected = filters.categories.includes(category);
    // Kept in the contract's order so the request is stable whatever the click order.
    const categories = TIMELINE_CATEGORIES.filter((c) =>
      c === category ? !selected : filters.categories.includes(c),
    );
    changeFilters({ ...filters, categories });
  }

  function retryFirstPage() {
    setFeed(LOADING);
    setReloadKey((k) => k + 1);
  }

  function loadMore() {
    const cursor = feed.nextCursor;
    if (cursor === null || feed.more === 'loading') return;
    const generation = generationRef.current;
    setFeed((prev) => ({ ...prev, more: 'loading' }));
    getMemberTimeline(contactId, {
      categories: filters.categories,
      from: filters.from,
      to: filters.to,
      cursor,
    })
      .then((page) => {
        if (generationRef.current !== generation) return;
        setFeed((prev) => ({
          ...prev,
          items: appendUnique(prev.items, page.items),
          nextCursor: page.nextCursor,
          more: 'idle',
        }));
      })
      .catch(() => {
        if (generationRef.current !== generation) return;
        // The rows already loaded stay; only the extra page failed.
        setFeed((prev) => ({ ...prev, more: 'error' }));
      });
  }

  const showRows = feed.status === 'ready' && zoneSettled;

  return (
    <Box>
      <ListToolbar
        filters={
          <Stack
            direction="row"
            role="group"
            aria-label={TIMELINE_COPY.filtersLabel}
            sx={{ gap: 1, flexWrap: 'wrap' }}
          >
            {TIMELINE_CATEGORIES.map((category) => {
              const selected = filters.categories.includes(category);
              return (
                <Chip
                  key={category}
                  label={TIMELINE_CATEGORY_LABELS[category]}
                  clickable
                  color={selected ? 'primary' : 'default'}
                  variant={selected ? 'filled' : 'outlined'}
                  aria-pressed={selected}
                  onClick={() => toggleCategory(category)}
                />
              );
            })}
          </Stack>
        }
        dateRange={{
          from: filters.from,
          to: filters.to,
          onFromChange: (from) => changeFilters({ ...filters, from }),
          onToChange: (to) => changeFilters({ ...filters, to }),
        }}
        onClear={() => changeFilters(NO_FILTERS)}
        canClear={hasFilters(filters)}
      />

      {feed.status === 'error' ? (
        <ScreenError
          variant="panel"
          error={feed.error}
          what="the timeline"
          onRetry={retryFirstPage}
          title={TIMELINE_COPY.errorTitle}
          retryMessage={TIMELINE_COPY.errorBody}
          preferServerMessage
          retryLabel={TIMELINE_COPY.retry}
          sx={{ mt: 1 }}
        />
      ) : (
        <Paper elevation={1} sx={{ px: 3, py: 1 }}>
          {!showRows && (
            <Box role="status" aria-label={TIMELINE_COPY.loadingLabel}>
              {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                <SkeletonRow key={`sk-${i}`} />
              ))}
            </Box>
          )}
          {showRows && feed.items.length === 0 && <EmptyState filtered={hasFilters(filters)} />}
          {showRows && feed.items.length > 0 && (
            <Box
              component="ol"
              aria-label={TIMELINE_COPY.listLabel}
              sx={{ listStyle: 'none', m: 0, p: 0 }}
            >
              {feed.items.map((item, i) => (
                <TimelineRow
                  key={item.id}
                  item={item}
                  contactId={contactId}
                  timeZone={timeZone}
                  last={i === feed.items.length - 1}
                />
              ))}
            </Box>
          )}
        </Paper>
      )}

      {showRows && feed.more === 'error' && (
        <Box role="alert" sx={{ mt: 1.5 }}>
          <Typography variant="body2">{TIMELINE_COPY.loadMoreErrorBody}</Typography>
        </Box>
      )}
      {showRows && feed.nextCursor !== null && (
        <Box sx={{ mt: 1.5, textAlign: 'center' }}>
          <Button variant="outlined" onClick={loadMore} disabled={feed.more === 'loading'}>
            {feed.more === 'loading'
              ? TIMELINE_COPY.loadingMore
              : feed.more === 'error'
                ? TIMELINE_COPY.retry
                : TIMELINE_COPY.loadMore}
          </Button>
        </Box>
      )}
    </Box>
  );
}

function TimelineRow({
  item,
  contactId,
  timeZone,
  last,
}: {
  item: TimelineItem;
  contactId: string;
  timeZone: string | null;
  last: boolean;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const maps = resolveMaps(theme);
  // The ledger link's window is on the location calendar, else the tenant's (DNKADM-0439).
  const calendarZone = useCalendarZone(timeZone);
  const family = timelineIconFamily(item.kind);
  const refused = family === 'refused';
  const tint = family !== null && !refused ? styleForKind(family, theme.palette.mode) : null;

  const sign = moneySign(item.kind);
  const unmoved = isUnmovedMoney(item.kind);
  const money =
    item.amountMinor !== null && item.currency !== null
      ? `${sign}${formatMinorMoney(item.amountMinor, item.currency)}`
      : null;
  const credits =
    item.creditChange !== null
      ? signedCredits(item.creditChange, formatCredits(Math.abs(item.creditChange)))
      : null;
  const cancelLines = cancelOutcomeLines(item.cancelOutcome);
  // DNKAPI-0676: why a declined card payment was declined; only on a DECLINE row (as the
  // ledger shows it only on a failed row), none when unknown or OTHER.
  const declineLine = item.kind === 'DECLINE' ? declineReasonLine(item.declineReason) : null;
  // DNKAPI-0687: a folded multi-lot movement, and an expiry's real lot boundary.
  const lotsLine = item.category === 'CREDITS' ? lotCountLine(item.lotCount) : null;
  const expiryLine = item.kind === 'CREDIT_EXPIRY' ? lotExpiryLine(item.lotExpiresAt, calendarZone) : null;
  const orderId = item.links.orderId;
  const [orderOpen, setOrderOpen] = useState(false);
  const creditColor =
    item.creditChange !== null && item.creditChange !== 0
      ? item.creditChange > 0
        ? maps.status.active.fg
        : t.error
      : inkAlpha(theme, 0.7);

  return (
    <Box
      component="li"
      sx={{
        display: 'flex',
        gap: 1.75,
        py: 1.375,
        borderBottom: last ? 'none' : `1px solid ${inkAlpha(theme, 0.05)}`,
      }}
    >
      {family !== null ? (
        <Box
          aria-hidden
          data-testid="timeline-kind-icon"
          sx={{
            width: 34,
            height: 34,
            borderRadius: '50%',
            bgcolor: refused ? t.warnSoft : tint?.bg,
            color: refused ? t.warnSoftInk : tint?.fg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            fontSize: 18,
          }}
        >
          <Icon name={refused ? REFUSED_ICON : (tint?.icon ?? '')} />
        </Box>
      ) : (
        // No icon for a kind this build does not know; the spacer keeps titles aligned.
        <Box aria-hidden sx={{ width: 34, flexShrink: 0 }} />
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{ fontSize: 13.5, fontWeight: 500, ...(refused ? { color: t.warnSoftInk } : {}) }}
        >
          {scrubBrand(timelineTitle(item))}
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: t.textMuted, mt: 0.25 }}>
          {formatTransactionTime(item.occurredAt, timeZone)}
          {' · '}
          {scrubBrand(timelineActorLabel(item.actor))}
        </Typography>
        {cancelLines.map((line) => (
          <Typography
            key={line}
            data-testid="timeline-cancel-outcome"
            sx={{ fontSize: 12.5, color: t.textMuted, mt: 0.25 }}
          >
            {scrubBrand(line)}
          </Typography>
        ))}
        {declineLine !== null && (
          <Typography
            data-testid="timeline-decline-reason"
            sx={{ fontSize: 12.5, color: t.textMuted, mt: 0.25 }}
          >
            {declineLine}
          </Typography>
        )}
        {[lotsLine, expiryLine].map((line) =>
          line === null ? null : (
            <Typography
              key={line}
              data-testid="timeline-credit-detail"
              sx={{ fontSize: 12.5, color: t.textMuted, mt: 0.25 }}
            >
              {line}
            </Typography>
          ),
        )}
        {(credits !== null || item.balance !== null) && (
          <Stack
            direction="row"
            sx={{ alignItems: 'center', gap: 1.25, mt: 0.375, fontSize: 12.5 }}
          >
            {credits !== null && (
              <Box component="span" sx={{ fontFamily: MONO, fontWeight: 600, color: creditColor }}>
                {`${credits} ${TIMELINE_COPY.credits}`}
              </Box>
            )}
            {item.balance !== null && (
              <Box component="span" sx={{ color: t.textSubtle }}>
                {`${TIMELINE_COPY.balance} `}
                <Box component="span" sx={{ fontFamily: MONO }}>
                  {formatCredits(item.balance)}
                </Box>
              </Box>
            )}
          </Stack>
        )}
        {(item.links.reservationUuid !== null || item.links.paymentId !== null || orderId !== null) && (
          <Stack direction="row" sx={{ gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
            {item.links.reservationUuid !== null && (
              <Link
                component={RouterLink}
                to={`/reservations?reservation=${encodeURIComponent(item.links.reservationUuid)}`}
                sx={{ fontSize: 12.5 }}
              >
                {TIMELINE_COPY.viewReservation}
              </Link>
            )}
            {item.links.paymentId !== null && (
              // There is no single-payment route: the member's payments ledger is the
              // closest existing destination (the Transactions tab's own link).
              <Link
                component={RouterLink}
                to={paymentLedgerLink(contactId, item.occurredAt, calendarZone)}
                sx={{ fontSize: 12.5 }}
              >
                {TIMELINE_COPY.viewPayment}
              </Link>
            )}
            {orderId !== null && (
              <Link
                component="button"
                type="button"
                onClick={() => setOrderOpen(true)}
                sx={{ fontSize: 12.5 }}
              >
                {`${TIMELINE_COPY.viewOrder} ${scrubBrand(item.links.orderNumber ?? String(orderId))}`}
              </Link>
            )}
          </Stack>
        )}
        {orderOpen && orderId !== null && (
          <TimelineOrderDialog
            contactId={contactId}
            orderId={orderId}
            orderNumber={item.links.orderNumber}
            onClose={() => setOrderOpen(false)}
          />
        )}
      </Box>
      {money !== null && (
        <Typography
          data-money-state={unmoved ? 'unmoved' : 'moved'}
          sx={{
            fontFamily: MONO,
            fontWeight: unmoved ? 400 : 600,
            fontSize: 13.5,
            flexShrink: 0,
            // A declined/pending amount never moved: muted, never the charge colour.
            color: unmoved ? t.textMuted : sign === '+' ? maps.status.active.fg : t.text2,
            ...(item.kind === 'DECLINE' ? { textDecoration: 'line-through' } : {}),
          }}
        >
          {money}
        </Typography>
      )}
    </Box>
  );
}

/**
 * The payments-ledger link for a timeline payment (DNKADM-0436 V-23). The ledger defaults to
 * the last 30 days, which hides an older payment, so the link carries a date window around the
 * payment's own date. The ledger has no paymentId filter. The window is the payment's day on
 * the calendar `zone` (the location's, else the tenant's, else the device: DNKADM-0439) plus or
 * minus one day, both ends inclusive, so the row stays on screen even if the ledger's own
 * bucketing zone differs. No parseable date falls back to the bare link.
 */
function paymentLedgerLink(contactId: string, occurredAt: string | null, zone: string | null): string {
  const base = `/transaction-ledger?tab=payments&contactId=${encodeURIComponent(contactId)}`;
  const at = occurredAt === null ? Number.NaN : Date.parse(occurredAt);
  if (Number.isNaN(at)) return base;
  const day = zonedDateOf(at, zone);
  return `${base}&from=${addDays(day, -1)}&to=${addDays(day, 1)}`;
}

function SkeletonRow() {
  return (
    <Stack direction="row" sx={{ gap: 1.75, py: 1.875 }}>
      <Skeleton variant="circular" width={34} height={34} />
      <Box sx={{ flex: 1 }}>
        <Skeleton width={220} height={13} />
        <Skeleton width={160} height={11} sx={{ mt: 0.5 }} />
      </Box>
    </Stack>
  );
}

function EmptyState({ filtered }: { filtered: boolean }) {
  const theme = useTheme();
  return (
    <Box sx={{ py: 6, textAlign: 'center' }}>
      <Icon name="history" sx={{ fontSize: 42, color: inkAlpha(theme, 0.2) }} />
      <Typography sx={{ fontSize: 15, fontWeight: 500, mt: 1 }}>
        {filtered ? TIMELINE_COPY.emptyFilteredTitle : TIMELINE_COPY.emptyTitle}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5, maxWidth: 360, mx: 'auto' }}>
        {TIMELINE_COPY.emptyBody}
      </Typography>
    </Box>
  );
}
